import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { buildZodSchema } from "../../services/dynamicForms/fieldSchema";
import useCascadingOptions from "../../hooks/dynamicForms/useCascadingOptions";
import { ACTION_DISPLAY_TYPES, ICONS } from "../../constants/enums";

// types
import { FieldDescriptor, SectionDescriptor } from "../../types/components/dynamicForms";

// ui components
import DynamicField from "./DynamicField";
import Alert from "../Common/Alerts/Alert";
import Action from "../Common/Action";

type FormValues = Record<string, unknown>;

interface DynamicFormRendererProps {
  sections: SectionDescriptor[];
  /** read once on mount — remount (key) to re-seed */
  initialValues: FormValues;
  /** server messages keyed by field name; applied as RHF errors of type "server" */
  serverErrors: Record<string, string>;
  submitLabel: string;
  submitting?: boolean;
  /** fires on every user change with the full value set and the changed field */
  onChange: (values: FormValues, changedName: string) => void;
  onSubmit: (values: FormValues) => void | Promise<void>;
}

const emptyValueFor = (field: FieldDescriptor): unknown =>
  field.kind === "phone" ? undefined : "";

/**
 * Renders one form from SectionDescriptor[]: section info (as an info Alert),
 * fields, submit. Workflow-agnostic — owns one useForm, validates via
 * buildZodSchema, and surfaces server errors passed in. Values out via onChange
 * (watch subscription, no watch(field)+useEffect) and onSubmit.
 *
 * Cascading selects are handled by useCascadingOptions: options load on mount
 * from every value collected so far (a parent can sit on an earlier step), the
 * schema and fields are built from its resolved fields, and every change reloads
 * dependents on this page.
 *
 * Submit stays disabled until the page is client-valid. "onTouched" validates a
 * field on first blur, then on change — no errors on the first keystroke.
 */
const DynamicFormRenderer = ({
  sections,
  initialValues,
  serverErrors,
  submitLabel,
  submitting,
  onChange,
  onSubmit,
}: DynamicFormRendererProps) => {
  const fields = useMemo(
    () => sections.reduce<FieldDescriptor[]>((acc, s) => acc.concat(s.fields), []),
    [sections]
  );

  // seeded once on mount — this page's fields
  const [defaultValues] = useState(() =>
    fields.reduce<FormValues>((acc, f) => {
      acc[f.name] = initialValues[f.name] ?? f.value ?? emptyValueFor(f);
      return acc;
    }, {})
  );

  // the cascade resolves tokens against every value collected so far, so a parent
  // on an earlier step still resolves; this page's defaults take precedence
  const [cascadeValues] = useState(() => ({ ...initialValues, ...defaultValues }));

  const { resolvedFields, fieldsByName, reloadDependents } = useCascadingOptions(fields, cascadeValues);
  const schema = useMemo(() => buildZodSchema(resolvedFields), [resolvedFields]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: "onTouched",
    resolver: zodResolver(schema),
    defaultValues,
  });

  const appliedServerErrors = useRef<string[]>([]);
  useEffect(() => {
    appliedServerErrors.current
      .filter((name) => !(name in serverErrors))
      .forEach((name) => clearErrors(name));
    Object.entries(serverErrors).forEach(([name, message]) =>
      setError(name, { type: "server", message })
    );
    appliedServerErrors.current = Object.keys(serverErrors);
  }, [serverErrors, setError, clearErrors]);

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (!name) return;
      onChange(values as FormValues, name);
      reloadDependents(name, values as FormValues, (dependent) =>
        setValue(dependent, "", { shouldValidate: true })
      );
    });
    return () => subscription.unsubscribe();
  }, [watch, onChange, reloadDependents, setValue]);

  return (
    <form className="bwp-dynamic-form" noValidate onSubmit={handleSubmit(onSubmit)}>
      {sections.map((section, i) => (
        <section key={i} className="bwp-dynamic-form__section">
          {section.infoHtml && (
            <Alert
              alertClasses="alert-info flex-row gap-8 bwp-dynamic-form__alert mb-0"
              message={
                <span
                  className="bwp-dynamic-form__info"
                  dangerouslySetInnerHTML={{ __html: section.infoHtml }}
                />
              }
              icon={ICONS.INFO}
            />
          )}
          {section.fields.map((field) => (
            <DynamicField key={field.name} field={fieldsByName[field.name]} control={control} />
          ))}
        </section>
      ))}
      <Action
        type="submit"
        displayType={ACTION_DISPLAY_TYPES.PRIMARY}
        className="btn-block mt-24"
        loading={submitting}
        disabled={submitting || !isValid}
        title={submitLabel}
      />
    </form>
  );
};

export default DynamicFormRenderer;